import React, { useMemo } from 'react'
import { getClassNames } from '../../../_utils/classnames'
import {
    IBadgeBehavior,
    NotificationNativeIosContentModel,
} from '../../../models/notification/notification-native-ios-content.model'
import { InfoCircleOutlined } from '@ant-design/icons'
import { DomainDto } from '../../../dtos/domain'
import { NATIVE_IOS_INTERRUPTION_LEVEL_CRITICAL } from '../../../constants'
import { hasFlag } from '../../../core/flags/has-flag'
import { IosBadgeBehaviorAction, IosInterruptionLevel, IosRelevanceScore } from '../enums'
import { Form, Input, Popover, Select, Space, Switch, Typography } from 'antd'
import TemplatizedTextField from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'

interface INotificationIosContent {
    value: NotificationNativeIosContentModel
    domain: DomainDto
    onChange: (content: NotificationNativeIosContentModel) => void
}

export const NotificationIosContent = (props: INotificationIosContent) => {
    const { value, onChange, domain } = props

    const workingModel = value.clone()
    const domainHasCriticalFlag = hasFlag(domain, NATIVE_IOS_INTERRUPTION_LEVEL_CRITICAL)

    const interruptionOptions = useMemo(() => {
        const options = [
            { value: IosInterruptionLevel.ACTIVE, label: 'Active' },
            { value: IosInterruptionLevel.TIME_SENSITIVE, label: 'Time Sensitive' },
            { value: IosInterruptionLevel.PASSIVE, label: 'Passive' },
        ]
        if (domainHasCriticalFlag) {
            options.push({ value: IosInterruptionLevel.CRITICAL, label: 'Critical' })
        }

        return options
    }, [domainHasCriticalFlag])

    return (
        <Form className={getClassNames('native-ios-content')} layout="vertical">
            <Form.Item className="native-ios-category" label="Category">
                <Select
                    defaultValue="GENERAL"
                    value={workingModel?.getCategory()}
                    size="middle"
                    onChange={(v) => {
                        const update = workingModel?.clone()
                        update?.setCategory(v)

                        onChange(update)
                    }}
                    options={[{ value: 'GENERAL', label: 'General' }]}
                />
            </Form.Item>

            <TemplatizedTextField
                disabled={workingModel.getSubtitle() === undefined}
                label={
                    <span className="native-ios-subtitle-switch">
                        <Switch
                            size="small"
                            defaultChecked={false}
                            checked={workingModel.getSubtitle() !== undefined}
                            onChange={(v) => {
                                const update = workingModel.clone()
                                if (v) {
                                    update.setSubtitle(workingModel.getSubtitle() ?? '')
                                } else {
                                    update.setSubtitle(undefined)
                                }

                                onChange(update)
                            }}
                            aria-label="Enable Subtitle"
                        />
                        <span className="label-with-component-l-r">Subtitle</span>
                        <Popover
                            classNames={{
                                content: getClassNames('notification-builder-popover', 'subtitle-popover'),
                            }}
                            content={
                                <Typography.Paragraph>
                                    Specify additional context about the purpose of the notification. Subtitles offer
                                    additional context in cases where the title alone isn’t clear. Subtitles aren’t
                                    displayed in all cases.
                                </Typography.Paragraph>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                }
                hideMacros={true}
                placeholder="Enter a subtitle"
                onChange={(v) => {
                    const update = workingModel?.clone()
                    update?.setSubtitle(v)

                    onChange(update)
                }}
                value={workingModel?.getSubtitle() ?? null}
            />

            <TemplatizedTextField
                disabled={value?.getSound() === undefined}
                label={
                    <span className="native-ios-custom-alert-switch">
                        <Switch
                            size="small"
                            checked={value?.getSound() !== undefined}
                            onChange={(v) => {
                                const update = value.clone()
                                if (v) {
                                    update?.setSound(workingModel.getSound() ?? '')
                                } else {
                                    update?.setSound(undefined)
                                }
                                onChange(update)
                            }}
                            aria-label="Enable Custom Alert Sound"
                        />
                        <span className="label-with-component-l-r">Custom Alert Sound</span>
                        <Popover
                            classNames={{
                                content: getClassNames('notification-builder-popover', 'custom-alert-popover'),
                            }}
                            content={
                                <Typography.Paragraph>
                                    If your application is bundled with a custom sound file for alerts you can enter the
                                    filename of that sound to use it for this notification.
                                </Typography.Paragraph>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                }
                hideMacros={true}
                value={value?.getSound()}
                placeholder="Enter the name of a sound file provided by your application"
                onChange={(v) => {
                    const update = workingModel?.clone()
                    update?.setSound(v)

                    onChange(update)
                }}
            />

            <Form.Item
                className="native-ios-priority"
                label={
                    <>
                        <span className="label-with-component-r">Interruption Level</span>
                        <Popover
                            classNames={{
                                content: getClassNames('notification-builder-popover', 'interruption-level-popover'),
                            }}
                            content={
                                <Typography.Paragraph>
                                    Choose the level of interruption this notification should have on the subscriber’s
                                    device:
                                    <ul>
                                        <li>
                                            <Typography.Text strong>Active</Typography.Text>: The system presents the
                                            notification immediately, lights up the screen, and can play a sound.
                                        </li>
                                        <li>
                                            <Typography.Text strong>Time Sensitive</Typography.Text>: The system
                                            presents the notification immediately, lights up the screen, and bypasses
                                            the mute switch to play a sound. This level breaks through focus mode so
                                            should be used sparingly and only when the notification requires immediate
                                            attention.
                                        </li>
                                        <li>
                                            <Typography.Text strong>Passive</Typography.Text>: The system adds the
                                            notification to the notification list without lighting up the screen or
                                            playing a sound.
                                        </li>
                                        {domainHasCriticalFlag && (
                                            <li>
                                                <Typography.Text strong>
                                                    Critical (Requires Apple Approval)
                                                </Typography.Text>
                                                : The system presents the notification immediately, lights up the
                                                screen, and can play a sound, but won’t break through system
                                                notification controls.
                                            </li>
                                        )}
                                    </ul>
                                </Typography.Paragraph>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </>
                }
            >
                <Select<IosInterruptionLevel>
                    value={IosInterruptionLevel[workingModel?.getInterruptionLevel().toUpperCase()!]}
                    size="middle"
                    onChange={(v) => {
                        const update = workingModel?.clone()
                        update?.setInterruptionLevel(v)

                        onChange(update)
                    }}
                    options={interruptionOptions}
                />
            </Form.Item>

            <Form.Item
                className="native-ios-relevance"
                label={
                    <>
                        <span className="label-with-component-r">Relevance Score</span>
                        <Popover
                            classNames={{
                                content: getClassNames('notification-builder-popover', 'relevance-popover'),
                            }}
                            content={
                                <Typography.Paragraph>
                                    The relevance score helps determine the importance of your notifications and display
                                    them accordingly in the iOS Notification Summary. Note that other factors, including
                                    adding an image, will also impact the importance.
                                </Typography.Paragraph>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </>
                }
            >
                <Select
                    value={value?.getRelevance()}
                    size="middle"
                    onChange={(v) => {
                        const update = workingModel?.clone()
                        update?.setRelevance(v)

                        onChange(update)
                    }}
                    options={[
                        { value: IosRelevanceScore.LOW, label: 'Low' },
                        { value: IosRelevanceScore.NORMAL, label: 'Normal' },
                        { value: IosRelevanceScore.HIGH, label: 'High' },
                    ]}
                />
            </Form.Item>

            <Form.Item
                className="native-ios-badge-behavior"
                label={
                    <>
                        <span className="label-with-component-r">Badge Behavior</span>
                        <Popover
                            classNames={{
                                content: getClassNames('notification-builder-popover', 'badge-behavior-popover'),
                            }}
                            content={
                                <Typography.Paragraph>
                                    The iOS badge count displays the number of unread notifications within your
                                    application. Choose how the badge count should update when this notification is
                                    received:
                                    <ul>
                                        <li>
                                            <Typography.Text strong>Increment</Typography.Text>: The current badge count
                                            will be incremented by the provided number.
                                        </li>
                                        <li>
                                            <Typography.Text strong>Set To</Typography.Text>: The badge count will be
                                            set to the exact number provided. Set to 0 to clear the badge count.
                                        </li>
                                        <li>
                                            <Typography.Text strong>Do Nothing</Typography.Text>: The current badge
                                            count will not be affected.
                                        </li>
                                    </ul>
                                </Typography.Paragraph>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </>
                }
            >
                <Space.Compact>
                    <Select
                        value={workingModel?.getBadgeBehavior()?.getAction()}
                        size="middle"
                        onChange={(v: IosBadgeBehaviorAction) => {
                            const update = workingModel?.clone()
                            const badgeBehavior: IBadgeBehavior = {
                                action: v ?? IosBadgeBehaviorAction.INCREMENT,
                                value: workingModel.getBadgeBehavior().getValue(),
                            }

                            switch (badgeBehavior.action) {
                                case IosBadgeBehaviorAction.INCREMENT:
                                case IosBadgeBehaviorAction.SET:
                                    badgeBehavior.value = 1
                                    break
                                case IosBadgeBehaviorAction.DO_NOTHING:
                                    badgeBehavior.value = null
                                    break
                                default:
                                    badgeBehavior.value = 1
                            }

                            update?.setBadgeBehavior(badgeBehavior)
                            onChange(update)
                        }}
                        options={[
                            { value: IosBadgeBehaviorAction.INCREMENT, label: 'Increment' },
                            { value: IosBadgeBehaviorAction.SET, label: 'Set To' },
                            { value: IosBadgeBehaviorAction.DO_NOTHING, label: 'Do Nothing' },
                        ]}
                    />
                    <Input
                        disabled={workingModel.getBadgeBehavior()?.getValue() === null}
                        type="number"
                        value={value.getBadgeBehavior().getValue()!}
                        onChange={(ev) => {
                            const update = workingModel?.clone()

                            const badgeBehavior = {
                                action: workingModel.getBadgeBehavior()?.getAction(),
                                value: parseInt(ev.target.value, 10),
                            }

                            update?.setBadgeBehavior(badgeBehavior)
                            onChange(update)
                        }}
                    />
                </Space.Compact>
            </Form.Item>

            <Form.Item>
                <TemplatizedTextField
                    disabled={value?.getTargetContentId() === undefined}
                    label={
                        <span className="native-ios-target-content-id-switch">
                            <Switch
                                size="small"
                                defaultChecked={false}
                                checked={value.getTargetContentId() !== undefined}
                                onChange={(v) => {
                                    const update = value.clone()

                                    update?.setTargetContentId(v ? workingModel.getTargetContentId() ?? '' : undefined)

                                    onChange(update)
                                }}
                                aria-label="Enable Target Content ID"
                            />
                            <span className="label-with-component-l-r">Target Content ID</span>
                            <Popover
                                classNames={{
                                    content: getClassNames('notification-builder-popover', 'target-content-id-popover'),
                                }}
                                content={
                                    <Typography.Paragraph>
                                        The Target Content ID is used to target a specific window in a multi-scene app
                                        or a specific experience in an App Clip using an invocation URL.
                                    </Typography.Paragraph>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </span>
                    }
                    hideEmojis={true}
                    hideMacros={true}
                    placeholder="Enter the Target Content ID"
                    value={value.getTargetContentId()}
                    onChange={(v) => {
                        const update = workingModel?.clone()
                        update?.setTargetContentId(v)

                        onChange(update)
                    }}
                />
            </Form.Item>
        </Form>
    )
}
